// Get an answer by question id
export const getAnswerById = (answers, questions, id) => {
    const idx = questions.findIndex((q) => q.id === id);
    return idx === -1 ? undefined : answers[idx];
};

// Check if a medication name requires dosage information
function needsDosage(name) {
    return name === "Spironolactone" || name === "Tretinoin/Tazarotene";
}

// Per-type validators
export const validators = {
    "personal-info": (a) => a && a.name?.trim() && a.dateOfBirth?.trim(),
    "contact-info": (a) => a && a.phoneNumber?.trim() && a.email?.trim(),
    sex: (a) => typeof a === "string" && a.trim().length > 0,
    treatment: (a) => typeof a === "string" && a.trim().length > 0,
    state: (a) => typeof a === "string" && a.trim().length > 0,

    // Accept normalized object or legacy string
    payment: (a) => {
        if (!a) return false;
        if (typeof a === "string") return a.trim().length > 0;
        if (typeof a === "object") {
            const t = String(a.type || "").toLowerCase();
            return ["insurance", "self-pay", "out-of-pocket", "card"].includes(t);
        }
        return false;
    },

    duration: (a) => typeof a === "string" && a.trim().length > 0,
    text: (a) => a !== undefined && a !== null && String(a).trim().length >= 0,
    "other-meds": (a) => {
        if (!a) return false;
        if (a.has === "n") return true;
        return Array.isArray(a.meds) && a.meds.every((m) => m && m.trim());
    },
    "menstrual-cycle": (a) => typeof a === "string" && a.trim().length > 0,
    contraceptives: (a) => {
        if (!a) return false;
        // Array format: empty array is valid (represents "None of the above")
        if (Array.isArray(a)) return true;
        // Object format: must have selections array, and if "Other" is selected, must have otherText
        if (typeof a === "object" && Array.isArray(a.selections)) {
            // Empty selections array is valid (represents "None of the above")
            if (a.selections.length === 0) return true;
            if (a.selections.includes("Other") && !String(a.otherText || "").trim()) return false;
            return true;
        }
        // Legacy string format
        return String(a).trim().length > 0;
    },
    "photo-upload": (a) => Array.isArray(a) && a.length >= 3,
    address: (a) => typeof a === "string" && a.trim().length > 0,
    "credit-card": (a) => a && a.brand && a.last4 && a.exp_month && a.exp_year,
    maternity: (a) => {
        if (!a || !Array.isArray(a.selections) || a.selections.length === 0) return false;
        if (a.selections.includes("Currently on birth control")) {
            if (!Array.isArray(a.birthControlMethods) || a.birthControlMethods.length === 0)
                return false;
            if (
                a.birthControlMethods.includes("Other") &&
                !String(a.otherBirthControl || "").trim()
            )
                return false;
        }
        return true;
    },
    tried: (a) => Array.isArray(a) && a.length > 0,
    prescriptions: (a) => {
        if (!Array.isArray(a)) return false;
        // Empty array is valid
        if (a.length === 0) return true;
        // Accept formats:
        // 1. Acne format: array of objects with name, duration, help, dosage (dosage required for Spironolactone/Tretinoin)
        // 2. Non-Acne format: array of objects with name, duration, help (no dosage)
        // 3. Legacy: array of strings (medication names only)
        return a.every((m) => {
            if (typeof m === "string") {
                // Legacy format: just a string
                return m.trim().length > 0;
            }
            // Object format: must have name, duration, help
            if (!m || !m.name || !m.duration || !m.help) return false;
            // Acne format: if name is "Other", need customName; if needsDosage, need dosage
            if (m.name === "Other" && !m.customName) return false;
            if (needsDosage(m.name) && !m.dosage) return false;
            return true;
        });
    },
    "insurance-method": (a) => typeof a === "string" && ["photos", "id", "skip"].includes(a),
    "insurance-info": (a) => {
        if (!a || !a.memberID || !a.policyOwner || !a.policyOwner.relationship) return false;
        if (a.policyOwner.relationship === "Self") return true;
        const p = a.policyOwner;
        return Boolean(p.fname && p.lname && p.gender && p.dob);
    },
    "insurance-photos": (a) => Array.isArray(a?.cardFront) && a.cardFront.length >= 1,
};

// Check if a question is valid given current visibility
export const isQuestionValid = (q, visibleQuestions, answers, questions) => {
    if (q.optional) return true;
    const isVisible = visibleQuestions.some((vq) => vq.id === q.id);
    if (!isVisible) return true;

    const a = getAnswerById(answers, questions, q.id);
    const validate =
        validators[q.type] ||
        (() => a !== undefined && a !== null && (typeof a === "object" ? true : String(a).trim() !== ""));
    return Boolean(validate(a));
};

// Build a simple mirror of question->answer for submission preview or storage
export const buildPayload = (questions, answers) => {
    const payload = {};
    for (const q of questions) {
        const a = getAnswerById(answers, questions, q.id);
        payload[q.id] = a;
    }
    payload.completedAt = new Date().toISOString();
    return payload;
};
